import React, { useState } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { MaterialReactTable } from "material-react-table";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import isMock from "../../config";
import {useMaterialUIController} from "../../context";

// Recursive search function for nested filtering (same as your code)
const searchRowRecursively = (row, columnId, filterValue) => {
    const value = row.getValue(columnId) || "";
    if (value.toString().toLowerCase().includes(filterValue.toLowerCase())) {
        return true;
    }
    if (row.subRows?.length) {
        return row.subRows.some((subRow) => searchRowRecursively(subRow, columnId, filterValue));
    }
    return false;
};

function Users() {
    // State to manage car-dialog
    const [carsDialogOpen, setCarsDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [controller] = useMaterialUIController();
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [globalFilter, setGlobalFilter] = useState("");
    const { darkMode } = controller;

    // Fetch users with React Query
    const { data, isLoading, error } = useQuery({
        queryKey: ["palUsers", pagination.pageIndex, pagination.pageSize, globalFilter],
        queryFn: async ({ queryKey }) => {
            const [, pageIndex, pageSize, filter] = queryKey;
            const skip = pageIndex * pageSize;

            const url =
                `https://blue-parking-invite.bluept.dev/api/pal-users/search` +
                `?limit=${pageSize}&skip=${skip}&filter=${encodeURIComponent(filter || "")}`;

            const response = await fetch(url);
            if (!response.ok) throw new Error("Failed to fetch pal users");
            return response.json(); // { ok, users, total }
        },
        keepPreviousData: true,
    });

    const users = data?.users || [];
    const rowCount = Number(data?.total || 0);

    // Delete handler
    const handleDeleteUser = async (userId) => {
        if (!window.confirm("Are you sure you want to delete this user?")) {
            return;
        }
        try {
            const response = await fetch(`http://localhost:3001/users/${userId}`, {
                method: "DELETE",
            });
            if (!response.ok) {
                throw new Error("Failed to delete user");
            }
            alert("User deleted successfully");
            refetch(); // Re-fetch so table updates
        } catch (err) {
            alert(`Error: ${err.message}`);
        }
    };

    // When you click the “Cars” cell
    const handleShowCars = (user) => {
        setSelectedUser(user);
        setCarsDialogOpen(true);
    };

    // Define table columns
    const columns = [
        { accessorKey: "firstname", header: "First Name" },
        { accessorKey: "lastname", header: "Last Name" },

        { accessorKey: "id", header: "id" },
        { accessorKey: "devicesCount", header: "Devices" },
        {
            id: "places",
            header: "Places",
            Cell: ({ row }) => {
                const places = row.original.places || [];
                return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        {places.map((p) => (
                            <span
                                key={p.id}
                                style={{
                                    color: darkMode ? "#7DD3FC" : "#0B63CE",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                    fontWeight: 700,
                                }}

                                onClick={() => {
                                    // if you have a places page, navigate there; otherwise just copy id
                                    navigator.clipboard?.writeText(p.id);
                                }}
                                title={p.id}
                            >
              {p.name}
            </span>
                        ))}
                    </div>
                );
            },
        },
    ];
    // MUI Theme customization
    const theme = createTheme({
        components: {
            MuiTablePagination: {
                styleOverrides: {
                    root: { backgroundColor: "#f5f5f5", color: "#000" },
                    selectIcon: { color: "#000" },
                },
            },
            MuiSvgIcon: { styleOverrides: { root: { color: "#000" } } },
            MuiInputBase: { styleOverrides: { root: { color: "#000" } } },
        },
    });

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3} px={2}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <MDTypography variant="h4">Users</MDTypography>
                </MDBox>

                {isLoading ? (
                    <MDTypography variant="h6">Loading users...</MDTypography>
                ) : error ? (
                    <MDTypography variant="h6" color="error">
                        Error: {error.message}
                    </MDTypography>
                ) : (
                    <ThemeProvider theme={theme}>
                        <MaterialReactTable
                            columns={columns}
                            data={users}
                            enableGlobalFilter

                            manualPagination
                            manualFiltering
                            rowCount={rowCount}

                            state={{ isLoading, pagination, globalFilter }}
                            onPaginationChange={setPagination}
                            onGlobalFilterChange={(val) => {
                                setGlobalFilter(val);
                                setPagination((p) => ({ ...p, pageIndex: 0 }));
                            }}

                            muiTableHeadProps={{
                                sx: {
                                    display: "table-header-group",
                                    backgroundColor: "rgb(0,255,234)",
                                    color: "white",
                                },
                            }}

                            //
                            muiBottomToolbarProps={{
                                sx: {
                                    backgroundColor: darkMode ? '#1e293b' : '#fff',
                                    color: darkMode ? '#fff' : '#1e293b',
                                    ".MuiFormLabel-root": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSelect-icon": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSelect-select": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiBox-root.MuiButtonBase-root" : {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                        // pick a dimmer color for disabled icons
                                        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                    },
                                }
                            }}
                            muiTopToolbarProps={{
                                sx: {
                                    backgroundColor: darkMode ? '#1e293b' : '#fff',
                                    color: '#fff',
                                    ".MuiSvgIcon-root" : {

                                        color: darkMode ? '#fff' : '#1e293b',

                                    },
                                    ".MuiTableSortLabel-iconDirectionAsc": {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    }
                                },

                            }}
                            muiTableBodyCellProps={{
                                sx: {
                                    backgroundColor: darkMode ? "#1e293b" : "#ffffff",
                                    color: darkMode ? "#ffffff" : "#000",
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? "#ffffff" : "#000",
                                    }
                                },
                            }}
                            muiTableHeadCellProps={{
                                sx: {
                                    backgroundColor: darkMode ? "#1e293b" : "#0098CF",
                                    color: "white",
                                    fontWeight: "bold",
                                    ".MuiSvgIcon-root" : {
                                        color: darkMode ? "#ffffff" : "#000",
                                    },
                                    // then specifically target the sort label icon:
                                    '& .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    // likewise for iconDirectionAsc / iconDirectionDesc if needed
                                    '& .MuiTableSortLabel-iconDirectionAsc': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    '& .MuiTableSortLabel-iconDirectionDesc': {
                                        color: darkMode ? '#fff !important' : '#000 !important',
                                    },
                                    '& .MuiButtonBase-root.Mui-disabled .MuiSvgIcon-root': {
                                        // pick a dimmer color for disabled icons
                                        color: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                    },
                                    '& .MuiInputBase-input': {
                                        color: darkMode ? '#fff' : '#1e293b',
                                    }
                                },
                            }}
                            //
                            muiTableContainerProps={{
                                sx: {
                                    width: "100%",
                                    overflowX: "auto",
                                    backgroundColor: "#15aff4",
                                    color: "white",
                                },
                            }}
                        />
                    </ThemeProvider>
                )}
            </MDBox>
            <Footer />

            {/* Dialog to display cars (license plates) for selectedUser */}
            <Dialog open={carsDialogOpen} onClose={() => setCarsDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{color: "#000"}}>Cars</DialogTitle>
                <DialogContent sx={{color: "#000"}}>
                    {selectedUser?.cars && selectedUser.cars.length > 0 ? (
                        selectedUser.cars.map((car, idx) => (
                            <div key={idx} style={{ marginBottom: "8px" }}>
                                • {car.licensePlate}
                            </div>
                        ))
                    ) : (
                        <div>No cars</div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCarsDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    );
}

export default Users;
